/**
 * Builds a Google Maps directions URL from an ordered list of "lat,lng" strings.
 * @param {string[]} pointsLatLng - e.g. ["48.8566,2.3522", "43.2965,5.3698"]
 * @returns {string|null}
 */
export function buildGoogleMapsDirUrl(pointsLatLng) {
	const clean = (pointsLatLng || []).filter(Boolean);
	if (clean.length === 0) return null;
	const encoded = clean.map((p) => encodeURIComponent(p));
	return `https://www.google.com/maps/dir/${encoded.join('/')}/`;
}

/**
 * Builds an Android geo: URI for multi-stop navigation.
 * @param {string[]} pointsLatLng - e.g. ["48.8566,2.3522", "43.2965,5.3698"]
 * @returns {string|null}
 */
export function buildGeoMultiUrl(pointsLatLng) {
	const clean = (pointsLatLng || []).filter(Boolean);
	if (clean.length === 0) return null;
	const q = clean.join('+to:');
	return `geo:0,0?q=${encodeURIComponent(q)}`;
}

/**
 * Opens the navigation route in an external app (Google Maps on desktop/iOS, geo: URI on Android).
 * @param {Array<[number, number]>} waypoints - Array of [lng, lat] pairs.
 */
export function openNavigationInApp(waypoints) {
	if (!waypoints?.length) return;

	const points = waypoints.map(([lng, lat]) => `${lat},${lng}`);
	const geoUrl = buildGeoMultiUrl(points);
	const googleMapsUrl = buildGoogleMapsDirUrl(points);

	if (!geoUrl || !googleMapsUrl) return;

	const isAndroid = /Android/i.test(navigator.userAgent);
	if (!isAndroid) {
		window.open(googleMapsUrl, '_blank');
		return;
	}

	let cancelled = false;
	const cancelFallback = () => {
		cancelled = true;
		cleanup();
	};
	const onVisibility = () => {
		if (document.visibilityState === 'hidden') cancelFallback();
	};
	const cleanup = () => {
		window.removeEventListener('blur', cancelFallback);
		window.removeEventListener('pagehide', cancelFallback);
		document.removeEventListener('visibilitychange', onVisibility);
	};

	window.addEventListener('blur', cancelFallback);
	window.addEventListener('pagehide', cancelFallback);
	document.addEventListener('visibilitychange', onVisibility);

	window.location.href = geoUrl;

	setTimeout(() => {
		cleanup();
		if (!cancelled) window.open(googleMapsUrl, '_blank');
	}, 2000);
}
